import React, { useState, useCallback, useEffect } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { message } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import AssignComponent from '@bringg/react-components/dist/components/assign-component/assign-component';
import type { Driver } from '@bringg/types';

import AssignDriverSelectItem from '../../orders/assign-driver-select-item/assign-driver-select-item';
import { getAssignDriverTranslations } from '../../orders/order-details-translations';
import useStores from '../../../recipes/use-stores';

import './styles.scss';

interface Props {
	taskId: number;
	driverId: number;
	isAllowedToAssignSpecificTask: boolean;
}

export const AssignSelect = observer(({ taskId, driverId, isAllowedToAssignSpecificTask }: Props) => {
	const { t } = useTranslation();

	const {
		driversStore: { all: drivers, fetchByTeam, get: getDriverById, driversByTeam },
		tasksStore,
		alertsStore
	} = useStores();

	const [isDriversLoading, setIsDriversLoading] = useState(false);
	const [driversOfOrderTeams, setDriversOfOrderTeams] = useState([]);

	const task = alertsStore.getTasks.get(taskId);
	// BE AWARE that task won't be changed since the task completely will be rewritten or
	// the `user_ids_with_available_inventories` will be changed

	useEffect(() => {
		const driver = getDriverById(driverId);

		if (driver && !driversOfOrderTeams.find(teamDriver => teamDriver.id === driver.id)) {
			// the driver needs to be added for proper rendering in assign select
			setDriversOfOrderTeams(driversOfOrderTeams.concat([driver]));
		}
	}, [driverId, drivers, driversByTeam]);

	const assignDriverTranslations = getAssignDriverTranslations(t);

	// TODO: will be refactored to one solution of loading drivers for tasks
	// jira item: BIL-423
	const loadDrivers = async () => {
		if (!task || driversOfOrderTeams.length > 1) {
			return;
		}

		setIsDriversLoading(true);

		const teamIds = toJS(task.team_ids);
		let driversByTeam: Driver[] = [];

		await Promise.all(teamIds.map(fetchByTeam))
			.then(res => {
				res.forEach(teamDrivers => {
					driversByTeam = driversByTeam.concat(teamDrivers);
				});
			})
			.catch(() => message.error(t('ERRORS.ERROR_OCCURRED')));

		setDriversOfOrderTeams(driversByTeam);
		setIsDriversLoading(false);
	};

	const renderDriver = useCallback(
		driver => {
			return <AssignDriverSelectItem driver={driver} task={task} />;
		},
		[task]
	);

	const handleDriverSelected = useCallback(
		(driverId: number) => {
			tasksStore.update(taskId, { id: taskId, user_id: driverId || null });
		},
		[taskId]
	);

	return (
		// TODO: will be refactored to one solution of loading drivers for tasks
		// jira item: BIL-423
		<div className="assign-driver-select" onClick={loadDrivers}>
			<AssignComponent
				loading={isDriversLoading}
				items={driversOfOrderTeams}
				renderOption={renderDriver}
				selectedItemId={driverId}
				onSelectedItem={handleDriverSelected}
				translations={assignDriverTranslations}
				enableUnassign={Boolean(driverId)}
				disabled={!isAllowedToAssignSpecificTask}
				dropdownClassName="exceptions-table-assign-select"
			/>
		</div>
	);
});
